export const IMAGE_DESCRIPTION_PROMPT = `
  Please provide a detailed description of this YouTube thumbnail image covering all visible elements. Be specific about size, positioning, and prominence of each element. Just return the description of the image, don't include any other text or preamble. Return the result in {{language}} language:

  1. Main Subject Identification (Prominence and Focus):

  What is the main subject of the thumbnail? (person, product, scene)
  Approximately what percentage of the frame does it occupy? (30%, 50%, etc.)
  Is the main subject highlighted in any way? (borders, glow effects, contrast lighting)
  For people: describe facial expressions in detail (surprise, anger, joy) and their intensity

  2. Text Elements and Legibility:

  List ALL text exactly as written, noting font size (large, medium, small)
  Describe text styling (bold, outlined, shadowed, color)
  Note text positioning within the frame
  Estimate text-to-image ratio (percentage of thumbnail covered by text)

  3. Visual Hierarchy and Composition:

  Describe the arrangement of elements from most to least prominent
  Identify the apparent focal point where the eye is drawn first
  Note any arrows, pointers, or directional cues guiding attention
  Are elements carefully arranged or randomly placed?

  4. Color Scheme and Contrast:

  Identify the 2-3 dominant colors and their relationship (complementary, analogous)
  Describe the contrast level between subject and background
  Note any color highlighting used to draw attention

  5. Additional Visual Elements:

  Background: Is it simple/complex, blurred/clear, contextual/abstract?
  Overlays: Emojis, icons, graphics, effects (explosions, action lines)
  Borders, frames, or graphical elements
  Any before/after comparisons or visual juxtapositions

  6. Intrigue Factor:

  Describe any elements creating curiosity or surprise
  Note any visual "hooks" designed to trigger interest
  Is there an implied story or scenario that creates questions?

  7. Technical Observations:

  Image quality (high-resolution, blurry, pixelated)
  Element placement relative to edges (potential cropping issues)
  Would small details remain visible at mobile size?

  ----
  Note: Please be thorough and specific. This detailed description will serve as the foundation for a comprehensive thumbnail evaluation based on attention metrics, content clarity, contextual alignment, and technical optimization.
`;

export const IMAGE_SCORE_PROMPT = `
  this is my formula to evaluate a youtube thumbnail image, use it to evaluate and score (integer between 1 and 100) the given image. Return the result including overall score, list of categories with name, explanation and score in {{language}} language. 
  Important: ALL scores MUST be whole numbers (integers) between 1 and 100, and explanation is a short explanation why it got the score. DO NOT MISS ANY CATEGORY.
  Return the result in JSON format:
  {
    "overallScore": integer,
    "categories": [
      {
        "name": string,
        "explanation": string,
        "score": integer
      },
      ...
    ]
  }

  ---
  ## Here is the formula:

  ## 1. Attention Metrics (45%)

  ### Facial Impact (15%)
  How to evaluate:

  - Score 100:
    Human face with exaggerated expression (surprise, shock, excitement, sadness, anger, happiness, fear, disgust, scared, etc.)
    Face occupies at least 30% of thumbnail area
    Eyes clearly visible and preferably wide open
    Expressive gestures that enhance emotion (hands on face, pointing, etc.)
    Face outlined or cutout from background for emphasis
    Direct eye contact with viewer

  - Score 60:
    Human face with moderate expression
    Face occupies 15-30% of thumbnail area
    Standard neutral expression
    No enhancing gestures
    Natural integration with background

  - Score 20:
    No human face present
    Face turned away from camera
    Face occupies less than 15% of thumbnail
    Multiple small faces with equal prominence
    Emotionless or bland expression
    Cannot see eyes clearly

  Good example: Person with shocked expression, hands on cheeks, wide eyes, cutout from background
  Bad example: Abstract graphic, object, or back of person's head with no facial expression

  ### Visual Contrast (15%)
  How to evaluate:

  - Score 100:
    Subject stands out with strong color contrast against background
    Clean background that doesn't compete with subject
    Use of complementary colors (blue/yellow, red/green)
    White borders/outlines around key elements
    Deliberate lighting that highlights subject

  - Score 60:
    Moderate contrast between subject and background
    Some competing elements but subject still visible
    Neutral background that doesn't enhance subject

  - Score 20: 
    Subject blends with background
    Cluttered, busy background
    Monotone color scheme
    Multiple competing elements of similar brightness/color
    No visual separation between elements

  Good example: Yellow text on blue background with white-outlined subject
  Bad example: Blue subject on similar blue background with no separation
  
  ### Curiosity Gap (15%)
  How to evaluate:

  - Score 100:
    Creates immediate "I must know more" reaction
    Shows unexpected juxtapositions
    or Uses specific numbers/claims that seem surprising
    or Shows "impossible" or dramatic scenarios
    or "Before and after" contrast that implies transformation
    or Visual metaphor that tells a story

  - Score 60:
    Moderately interesting setup
    Implied value but not immediately compelling
    Generic "how to" or informational framing
    Standard topic with slight twist

  - Score 20:
    No mystery or curiosity element
    Generic product or scene
    No implied story or question
    Nothing unexpected or surprising
    No reason to click beyond basic information

  Good example: "SHOCKING SURPRISE REVEALED" with person's extreme reaction
  Bad example: Standard game/product screenshot with no hook

  ## 2. Content Clarity (35%)
  ### Object Recognition (5%)
  How to evaluate:

  - Score 100:
    Main subject occupies at least 30% of the frame
    Subject has clean cutout or distinct outline separating it from background
    Single dominant visual focus point
    High contrast between subject and background colors

  - Score 60: 
    Main subject identifiable but occupies less than 30% of frame
    Multiple competing elements with unclear focal priority
    Subject lacks distinct visual separation from background
    Moderate contrast between subject and background

  - Score 20:
    Main subject difficult to identify quickly (takes >1 second)    
    Multiple small, competing elements creating visual confusion
    No clear subject hierarchy or focal point
    Subject blends into background with minimal contrast
    Cluttered layout with numerous distracting elements

  Good example: Muscular person with clear body outline against distinct gym background; person with shocked expression with white outline against vibrant background
  Bad example: Game screenshot with multiple small elements and no clear focus; collage with multiple random pictures scattered throughout thumbnail

  ### Text Legibility (15%)
  How to evaluate:

  - Score 100:
    Maximum 3-5 words total in the entire thumbnail
    Bold, sans-serif fonts with clean edges
    Text size at least 1/5 of thumbnail height
    High contrast text colors against background (yellow/black, white/black, white/red)
    Text has outlines, shadows, or highlighting for maximum readability
    Each text element contains maximum 2-3 words
    No text in bottom-right corner (timestamp area)

  - Score 60:
    Some text elements too small for instant recognition
    Moderate contrast with background
    Inconsistent text styling or font choices
    Limited or no text enhancement (outlines/shadows)
    Text placement competes with visual elements

  - Score 20:
    More than 8 total words
    Decorative, script, or thin fonts that reduce readability
    Text too small to read at thumbnail size
    Poor contrast with background (similar colors)
    Text scattered throughout image without logical grouping
    Multiple font styles creating visual confusion
    Tiny text that becomes illegible when scaled down

  Good example: "MORE SALES" in large yellow text; "$599/DAY" in bright, bold text with outline; "SHOCKING SURPRISE REVEALED" with high contrast
  Bad example: Gothic/script font "This is where Miquella left"; tiny, low-contrast text in comic bubbles; paragraphs of small text speech bubbles

  ### Visual Hierarchy (15%)
  How to evaluate:

  - Score 100:
    Clear size differentiation between primary and secondary elements (primary 2-3x larger)
    Primary text uses attention-grabbing colors (yellow, red, white)
    Maximum 3 distinct visual elements organized by importance
    Critical information gets largest size and brightest color
    Text arranged in logical reading order (top-to-bottom, left-to-right)
    Balanced composition that guides the eye through content
    Strong focal point centered or positioned for immediate visibility

  - Score 60:
    Some size difference but not dramatic enough for instant recognition
    Moderate color differentiation between elements
    Some elements compete for attention
    Basic organization but lacks intentional visual flow

  - Score 20:
    All elements approximately the same size
    No color differentiation to establish importance
    6+ visual elements competing for attention
    Random placement of text and graphics
    No logical flow between elements
    Text and visuals scattered throughout without organization
    Elements placed without consideration of viewing priority

  Good example: Large "DREAMS" text with smaller subtitle and single focused image; "$599/day" as clear focal point with supporting element
  Bad example: Collage of random meme images with scattered text; cluttered gaming screen with multiple small elements; mass effect mod image with at least 10 different text elements

  ## 3. Contextual Alignment (10%)

  ### Content Relevance (10%)
  How to evaluate:

  - Score 100:
    Thumbnail creates a clear, self-contained narrative or concept
    Visual elements tell a complete story without needing text explanation
    Subject matter is immediately recognizable and concrete (not abstract)
    Clear emotional or informational value proposition is visually communicated
    A specific scenario, comparison, or transformation is clearly depicted
    Professional quality signals authentic, valuable content


  - Score 60:
    Thumbnail communicates a general content category but lacks specificity
    Basic narrative elements present but lacks cohesive storytelling
    Requires some interpretation to understand specific content
    Subject matter is identifiable but purpose may not be immediately clear
    Emotional appeal present but doesn't clearly indicate content value


  - Score 20:
    Visual elements appear random, chaotic, or unconnected
    No discernible narrative or content category communicated
    Relies on shock value rather than content representation
    Impossible to determine subject matter from visual elements alone
    Unprofessional quality suggests low-value content
    Appears deliberately misleading or deceptive

  Good example: Split-screen showing muscular person at gym and healthy food creates clear fitness content narrative without needing title
  Bad example: Random meme collage or extreme reaction face with no clear context about what the content actually contains

  ## 4. Technical Optimization (10%)

  ### Mobile Compatibility (5%)
  How to evaluate:

  - Score 100:
    Main elements remain clearly visible when scaled down to 150px width
    Contains only 2-3 primary elements for maximum clarity on small screens
    Uses a limited color palette (2-4 dominant colors) with strong contrast
    Text is large (minimum 1/5 of thumbnail height) and remains legible on mobile
    Simple composition that remains effective at all sizes
    Easily recognizable subject matter even on mobile phone screens
    No small details that become lost at smaller sizes

  - Score 60:
    Main elements visible but some detail or text legibility reduced at mobile size
    Contains 4-5 elements that create some complexity at small sizes
    Uses harmonious but less contrasting colors that remain distinguishable
    Text readable but requires more attention at small sizes
    Some smaller details lost but main concept remains clear
    Generally effective on mobile but optimal on desktop
    Requires slightly more focus to understand fully on small screens

  - Score 20:
    Critical elements become unreadable or indistinguishable at mobile size
    Contains 6+ competing elements creating visual chaos at small sizes
    Uses too many colors (6+ dominant colors) that blend together when scaled down
    Text too small to read on mobile devices
    Important details completely lost when scaled down
    Requires viewing at full size to understand content
    Appears as an indecipherable jumble on small screens
  
  Good example: Large facial reaction with bold text on a simple background; thumbnail with "$599/day" text and focused subject with minimal elements; "DREAMS" text with astronaut figure against dark background
  Bad example: Cluttered layout with multiple small images, text bubbles, and interface elements; "Crick's Picks" thumbnail with 6+ sports figures and excessive background elements; thumbnail with small, illegible text and too many visual elements


  ### Visual Clarity (2.5%)
  How to evaluate:

  - Score 100:
    High-resolution images with sharp, clean edges and no pixelation
    Professional-quality rendering of all visual elements
    No compression artifacts or blurriness
    Clear separation between subject and background
    Proper use of focus to highlight important elements
    Crisp, clean edges on text and graphical elements
    Maintains quality at full 1280×720 resolution

  - Score 60:
    Acceptable image quality with minor imperfections
    Some elements could be sharper or more refined
    Slight compression visible but not distracting
    Adequate separation between elements
    Generally clear imagery but lacks professional polish
    Minor inconsistencies in quality between elements
    Functional but not optimized visual presentation

  - Score 20:
    Low-resolution elements with visible pixelation
    Blurry, fuzzy, or out-of-focus important elements
    Heavy compression artifacts that degrade image quality
    Poor quality screenshots or image captures
    Amateur-looking graphics or design elements
    Inconsistent resolution between different elements
    Overall unprofessional visual appearance

  Good example: Crystal clear shark image with human reaction; clean, professional text treatment on gradient backgrounds with sharp figure cutouts; high-resolution dramatic lighting on astronaut figure
  Bad example: Poorly drawn cartoon figures with jagged edges; low-resolution screenshots embedded within thumbnails; blurry, pixelated text and graphics with inconsistent quality

  ### Safe Zone Utilization (2.5%)
  How to evaluate:

  - Score 100:
    Key elements positioned at least 10% away from all edges
    No important content in the bottom-right corner (timestamp area)
    Focal point positioned in the optimal viewing area (generally center-left)
    Balanced composition that guides the eye through the content
    Strategic use of negative space around critical elements
    Accommodates platform interface elements without content loss
    Creates visual breathing room that enhances clarity

  - Score 60:
    Some elements near edges but main subject in safe zone
    Minor content potentially affected by interface elements
    Focal point properly positioned but some secondary elements at risk
    Generally balanced composition with minor issues
    Limited buffer space between content and edges
    Most critical content safe from being cropped
    Functional spacing but not optimized for all platforms


  -Score 20:
    Critical elements positioned at extreme edges likely to be cropped
    Important text or faces in the timestamp corner
    Key content extends to the very edge of the frame
    Unbalanced composition that feels awkward or incomplete
    No consideration for platform interface elements
    Text or faces cut off by the thumbnail boundaries
    Overall poor spatial awareness in composition

  Good example: Face and text positioned away from edges with clear margins; important elements centered with adequate spacing from borders; thumbnail with text and figures positioned away from timestamp area
  Bad example: Important information placed directly in timestamp corner; text running against or very close to edges; images or faces cut off by frame edges with no margin

  ## Scoring Example:
  For a thumbnail scoring:

  - Attention Metrics: 80/100 × 45 = 36
  - Content Clarity: 70/100 × 35 = 24.5
  - Contextual Alignment: 80/100 × 10 = 8
  - Technical Optimization: 80/100 × 10 = 10

  Total score: 76.5/100
  ---
  Here is the description of the image: {{description}}
  `;

export const IMAGE_SCORE_PROMPT_WITH_DESCRIPTION = `
  this is my formula to evaluate a youtube thumbnail image, use it to evaluate and score (integer between 1 and 100) the given image. Return the result including overall score, list of categories with name, explanation and score in {{language}} language. 
  Important: ALL scores MUST be whole numbers (integers) between 1 and 100, and explanation is a short explanation why it got the score. DO NOT MISS ANY CATEGORY.
  Return the result in JSON format:
  {
    "overallScore": integer,
    "categories": [
      {
        "name": string,
        "explanation": string,
        "score": integer,
        "subCategories": [
          {
            "name": string,
            "explanation": string,
            "score": integer
          }
        ]
      },
      ...
    ]
  }

  ---
  ## Here is the formula:

  ## 1. Attention Metrics (40%)

  ### Facial Impact (15%)
  How to evaluate:

  - Score 100:
    Human face with exaggerated expression (surprise, shock, excitement, sadness, anger, happiness, fear, disgust, scared, etc.)
    Face occupies at least 30% of thumbnail area
    Eyes clearly visible and preferably wide open
    Expressive gestures that enhance emotion (hands on face, pointing, etc.)
    Face outlined or cutout from background for emphasis
    Direct eye contact with viewer

  - Score 60:
    Human face with moderate expression
    Face occupies 15-30% of thumbnail area
    Standard neutral expression
    No enhancing gestures
    Natural integration with background

  - Score 20:
    No human face present
    Face turned away from camera
    Face occupies less than 15% of thumbnail
    Multiple small faces with equal prominence
    Emotionless or bland expression
    Cannot see eyes clearly

  Good example: Person with shocked expression, hands on cheeks, wide eyes, cutout from background
  Bad example: Abstract graphic, object, or back of person's head with no facial expression

  ### Visual Contrast (10%)
  How to evaluate:

  - Score 100:
    Subject stands out with strong color contrast against background
    Clean background that doesn't compete with subject
    Use of complementary colors (blue/yellow, red/green)
    White borders/outlines around key elements
    Deliberate lighting that highlights subject

  - Score 60:
    Moderate contrast between subject and background
    Some competing elements but subject still visible
    Neutral background that doesn't enhance subject

  - Score 20: 
    Subject blends with background
    Cluttered, busy background
    Monotone color scheme
    Multiple competing elements of similar brightness/color
    No visual separation between elements

  Good example: Yellow text on blue background with white-outlined subject
  Bad example: Blue subject on similar blue background with no separation
  
  ### Curiosity Gap (15%)
  How to evaluate:

  - Score 100:
    Creates immediate "I must know more" reaction
    Shows unexpected juxtapositions
    or Uses specific numbers/claims that seem surprising
    or Shows "impossible" or dramatic scenarios
    or "Before and after" contrast that implies transformation
    or Visual metaphor that tells a story

  - Score 60:
    Moderately interesting setup
    Implied value but not immediately compelling
    Generic "how to" or informational framing
    Standard topic with slight twist

  - Score 20:
    No mystery or curiosity element
    Generic product or scene
    No implied story or question
    Nothing unexpected or surprising
    No reason to click beyond basic information

  Good example: "SHOCKING SURPRISE REVEALED" with person's extreme reaction
  Bad example: Standard game/product screenshot with no hook

  ## 2. Content Clarity (30%)
  ### Object Recognition (4%)
  How to evaluate:

  - Score 100:
    Main subject occupies at least 30% of the frame
    Subject has clean cutout or distinct outline separating it from background
    Single dominant visual focus point
    High contrast between subject and background colors

  - Score 60: 
    Main subject identifiable but occupies less than 30% of frame
    Multiple competing elements with unclear focal priority
    Subject lacks distinct visual separation from background
    Moderate contrast between subject and background

  - Score 20:
    Main subject difficult to identify quickly (takes >1 second)    
    Multiple small, competing elements creating visual confusion
    No clear subject hierarchy or focal point
    Subject blends into background with minimal contrast
    Cluttered layout with numerous distracting elements

  Good example: Muscular person with clear body outline against distinct gym background; person with shocked expression with white outline against vibrant background
  Bad example: Game screenshot with multiple small elements and no clear focus; collage with multiple random pictures scattered throughout thumbnail

  ### Text Legibility (13%)
  How to evaluate:

  - Score 100:
    Maximum 3-5 words total in the entire thumbnail
    Bold, sans-serif fonts with clean edges
    Text size at least 1/5 of thumbnail height
    High contrast text colors against background (yellow/black, white/black, white/red)
    Text has outlines, shadows, or highlighting for maximum readability
    Each text element contains maximum 2-3 words
    No text in bottom-right corner (timestamp area)

  - Score 60:
    Some text elements too small for instant recognition
    Moderate contrast with background
    Inconsistent text styling or font choices
    Limited or no text enhancement (outlines/shadows)
    Text placement competes with visual elements

  - Score 20:
    More than 8 total words
    Decorative, script, or thin fonts that reduce readability
    Text too small to read at thumbnail size
    Poor contrast with background (similar colors)
    Text scattered throughout image without logical grouping
    Multiple font styles creating visual confusion
    Tiny text that becomes illegible when scaled down

  Good example: "MORE SALES" in large yellow text; "$599/DAY" in bright, bold text with outline; "SHOCKING SURPRISE REVEALED" with high contrast
  Bad example: Gothic/script font "This is where Miquella left"; tiny, low-contrast text in comic bubbles; paragraphs of small text speech bubbles

  ### Visual Hierarchy (13%)
  How to evaluate:

  - Score 100:
    Clear size differentiation between primary and secondary elements (primary 2-3x larger)
    Primary text uses attention-grabbing colors (yellow, red, white)
    Maximum 3 distinct visual elements organized by importance
    Critical information gets largest size and brightest color
    Text arranged in logical reading order (top-to-bottom, left-to-right)
    Balanced composition that guides the eye through content
    Strong focal point centered or positioned for immediate visibility

  - Score 60:
    Some size difference but not dramatic enough for instant recognition
    Moderate color differentiation between elements
    Some elements compete for attention
    Basic organization but lacks intentional visual flow

  - Score 20:
    All elements approximately the same size
    No color differentiation to establish importance
    6+ visual elements competing for attention
    Random placement of text and graphics
    No logical flow between elements
    Text and visuals scattered throughout without organization
    Elements placed without consideration of viewing priority

  Good example: Large "DREAMS" text with smaller subtitle and single focused image; "$599/day" as clear focal point with supporting element
  Bad example: Collage of random meme images with scattered text; cluttered gaming screen with multiple small elements; mass effect mod image with at least 10 different text elements

  ## 3. Content-to-Thumbnail Alignment (20%)
  ### Truth in Advertising (7%)
  How to evaluate:

  - Score 100:
    Thumbnail accurately represents actual video content
    No misleading elements or false promises
    Visuals directly relate to what viewers will see in the video
    No fake scenarios, manipulated images, or deceptive setups
    Avoids clickbait tactics that misrepresent content


  - Score 60:
    Thumbnail uses some exaggeration but core content is represented
    Creative presentation that maintains honesty about actual content
    Stylized presentation that still accurately represents video content

  - Score 20:
    Thumbnail suggests content not actually present in the video
    Uses fake or staged scenarios presented as real
    Shows extreme reactions to mundane content
    Uses misleading imagery (e.g., fake celebrity appearances)
    Contains deliberately shocking or inappropriate content for attention

  Good example: Gaming tutorial thumbnail showing actual gameplay moment discussed in video
  Bad example: Reaction face thumbnail for a video with no reactions or emotional moments

  ### Title-Thumbnail Synergy (7%)
  How to evaluate:

  - Score 100:
    Key terms from the title appear in the thumbnail
    Thumbnail visually reinforces the title's main concept
    Contains visual elements that directly relate to title keywords
    Clear connection between what the title promises and what the thumbnail shows
    Numerical elements in title (e.g., "$599/day", "Top 5") appear in thumbnail


  - Score 60:
    Thumbnail relates to title but doesn't strongly reinforce main message
    General connection to topic but lacks specific keyword reinforcement
    Requires viewers to make some connections themselves

  - Score 20:
    Thumbnail and title appear to be for different videos
    No visible connection between title keywords and thumbnail imagery
    Title mentions specific topics that are absent from thumbnail
    Contradictory messaging between title and visual elements
    Extremely long/rambling title with no clear relation to thumbnail


  Good example: Title "Earn $599/day" with thumbnail showing exact "$599/day" text and money imagery
  Bad example: Title about specific game mechanics with unrelated imagery from different games

  ### Topic Context (6%)
  How to evaluate:

  - Score 100:
    A viewer can immediately understand the video's specific topic from thumbnail alone
    Contains clear visual indicators of the video's subject matter
    Uses universally recognizable symbols/imagery for the content category
    Supports visual storytelling that communicates video premise
    Split-screen or comparative elements that show clear relationship


  - Score 60:
    Topic is suggested but requires title to fully understand specifics
    General category is clear but specific focus remains ambiguous
    Relevant imagery but lacks precise topic indicators

  - Score 20:

    Topic is ambiguous even with title context
    Random or generic imagery unrelated to content
    Confusing visual elements that don't connect to any clear topic
    Multiple unrelated elements creating visual confusion
    Abstract imagery with no clear subject focus
    Screenshot of unrelated material (social media, random websites)

  Good example: Fitness thumbnail clearly showing both gym equipment and healthy food for "Secret Routine"
  Bad example: Random meme collage for educational content with no topic indicators

  ## 4. Technical Optimization (10%)

  ### Mobile Compatibility (10%)
  How to evaluate:

  - Score 100:
    Main elements remain clearly visible when scaled down to 150px width
    Contains only 2-3 primary elements for maximum clarity on small screens
    Uses a limited color palette (2-4 dominant colors) with strong contrast
    Text is large (minimum 1/5 of thumbnail height) and remains legible on mobile
    Simple composition that remains effective at all sizes
    Easily recognizable subject matter even on mobile phone screens
    No small details that become lost at smaller sizes

  - Score 60:
    Main elements visible but some detail or text legibility reduced at mobile size
    Contains 4-5 elements that create some complexity at small sizes
    Uses harmonious but less contrasting colors that remain distinguishable
    Text readable but requires more attention at small sizes
    Some smaller details lost but main concept remains clear
    Generally effective on mobile but optimal on desktop
    Requires slightly more focus to understand fully on small screens

  - Score 20:
    Critical elements become unreadable or indistinguishable at mobile size
    Contains 6+ competing elements creating visual chaos at small sizes
    Uses too many colors (6+ dominant colors) that blend together when scaled down
    Text too small to read on mobile devices
    Important details completely lost when scaled down
    Requires viewing at full size to understand content
    Appears as an indecipherable jumble on small screens
  
  Good example: Large facial reaction with bold text on a simple background; thumbnail with "$599/day" text and focused subject with minimal elements; "DREAMS" text with astronaut figure against dark background
  Bad example: Cluttered layout with multiple small images, text bubbles, and interface elements; "Crick's Picks" thumbnail with 6+ sports figures and excessive background elements; thumbnail with small, illegible text and too many visual elements


  ### Visual Clarity (5%)
  How to evaluate:

  - Score 100:
    High-resolution images with sharp, clean edges and no pixelation
    Professional-quality rendering of all visual elements
    No compression artifacts or blurriness
    Clear separation between subject and background
    Proper use of focus to highlight important elements
    Crisp, clean edges on text and graphical elements
    Maintains quality at full 1280×720 resolution

  - Score 60:
    Acceptable image quality with minor imperfections
    Some elements could be sharper or more refined
    Slight compression visible but not distracting
    Adequate separation between elements
    Generally clear imagery but lacks professional polish
    Minor inconsistencies in quality between elements
    Functional but not optimized visual presentation

  - Score 20:
    Low-resolution elements with visible pixelation
    Blurry, fuzzy, or out-of-focus important elements
    Heavy compression artifacts that degrade image quality
    Poor quality screenshots or image captures
    Amateur-looking graphics or design elements
    Inconsistent resolution between different elements
    Overall unprofessional visual appearance

  Good example: Crystal clear shark image with human reaction; clean, professional text treatment on gradient backgrounds with sharp figure cutouts; high-resolution dramatic lighting on astronaut figure
  Bad example: Poorly drawn cartoon figures with jagged edges; low-resolution screenshots embedded within thumbnails; blurry, pixelated text and graphics with inconsistent quality

  ### Safe Zone Utilization (5%)
  How to evaluate:

  - Score 100:
    Key elements positioned at least 10% away from all edges
    No important content in the bottom-right corner (timestamp area)
    Focal point positioned in the optimal viewing area (generally center-left)
    Balanced composition that guides the eye through the content
    Strategic use of negative space around critical elements
    Accommodates platform interface elements without content loss
    Creates visual breathing room that enhances clarity

  - Score 60:
    Some elements near edges but main subject in safe zone
    Minor content potentially affected by interface elements
    Focal point properly positioned but some secondary elements at risk
    Generally balanced composition with minor issues
    Limited buffer space between content and edges
    Most critical content safe from being cropped
    Functional spacing but not optimized for all platforms


  -Score 20:
    Critical elements positioned at extreme edges likely to be cropped
    Important text or faces in the timestamp corner
    Key content extends to the very edge of the frame
    Unbalanced composition that feels awkward or incomplete
    No consideration for platform interface elements
    Text or faces cut off by the thumbnail boundaries
    Overall poor spatial awareness in composition

  Good example: Face and text positioned away from edges with clear margins; important elements centered with adequate spacing from borders; thumbnail with text and figures positioned away from timestamp area
  Bad example: Important information placed directly in timestamp corner; text running against or very close to edges; images or faces cut off by frame edges with no margin

  ## Scoring Example:
  For a thumbnail scoring:

  - Attention Metrics: 80/100 × 40 = 32
  - Content Clarity: 70/100 × 30 = 21
  - Content-to-Thumbnail Alignment: 80/100 × 20 = 16
  - Technical Optimization: 80/100 × 10 = 10

  Total score: 79/100
  ---
  Here is the title/description of the video: {{videoDescription}}
  ---
  Here is the description of the image: {{description}}
  `;

export const GET_IMAGE_IMPROVEMENTS_PROMPT = `
  Base on the evaluation results, give me some advices to improve the thumbnail. The improvements must point exactly the elements, objects, texts... listed in the image description. Return the result with a list of improvements {explanation, command, rank} in {{language}} language. The improvement commands to apply to the image must be short, concile as a command for an image editor, must target specific element, object, text listed in the image description, and in English language. For example: "Add a white border to the main character", or "Make the text \`Do you know\` bigger 20%". Only return the improvements array, no other text or preamble. DO NOT return more than 5 improvements, focus on the most important ones to make the thumbnail more effective. Also sort the improvements by rank, the higher the rank, the more important of improvement and can make the thumbnail get a better score. 
  Return the result in JSON format:
  {
    "improvements": [
      {
        "explanation": string,
        "command": string,
        "rank": integer
      },
      ...
    ]
  }
  ---
  Here is the evaluation results: {{results}}
  ---
  Here is the description of the image: {{description}}
  `;

export const IMAGE_IMPROVEMENTS_PROMPT = `
  Apply the following improvements to the image. Don't change the style of the image, just make the improvements. Make sure to return an image in {{aspectRatio}} aspect ratio:
  `;
